import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { DataProvider } from './context/DataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CampaignProvider } from './context/CampaignContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import TabbedCharacterSheet from './components/character-sheet/TabbedCharacterSheet';
import CharacterList from './components/CharacterList';
import Login from './components/Login';
import CampaignCreator from './components/CampaignCreator';
import CampaignView from './components/CampaignView';
import AppHeader from './components/layout/AppHeader';
import BeastFeastAnimationDemo from './components/animations/BeastFeastAnimationDemo';
import CompanionCreator from './components/CompanionCreator';
import CompanionSheet from './components/CompanionSheet';
import CharacterBuilder from './components/character-builder/CharacterBuilder';
import useCharacterCampaign from './hooks/useCharacterCampaign';
import { trackCharacterCreated, trackCharacterDeleted } from './services/analyticsService';

// Landing page components for unauthenticated users
import {
  LandingPage,
  PrivacyPolicy,
  TermsOfService,
  BetaAccessPending,
  Roadmap,
  About,
} from './components/landing';


// Direct Firestore imports
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';
import { db } from './firebase/config';

// Shared header for simple routes (companion, campaign creator)
function SimplePageLayout({ children }) {
  return (
    <div className="min-h-screen bg-background text-gray-200">
      <AppHeader />
      <main className="pt-[73px]">{children}</main>
    </div>
  );
}

// Component for Character List route
function CharacterListRoute() {
  const navigate = useNavigate();
  
  const handleSelectCharacter = (character) => {
    navigate(`/character/${character.id}`);
  };

  const handleCreateNew = () => {
    navigate('/characters/new/build');
  };

  return (
    <CharacterList 
      onSelectCharacter={handleSelectCharacter}
      onCreateNew={handleCreateNew}
    />
  );
}


// Component for Character Builder route (unified builder)
function CharacterBuilderRoute() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { characterId } = useParams(); // Get characterId from URL params

  const handleCharacterForge = async (newCharacter, allData) => {
    try {
      const classData = allData.classes[newCharacter.class];

      // Helper function to get armor data from appropriate source
      const getArmorData = (armorId) => {
        if (!armorId) return null;

        // First check core armor
        if (allData.armor && allData.armor[armorId]) {
          return allData.armor[armorId];
        }

        // Then check campaign frame armor (stored as array)
        if (newCharacter.campaignFrame !== 'core' && allData.campaignFrames && allData.campaignFrames[newCharacter.campaignFrame]) {
          const frameData = allData.campaignFrames[newCharacter.campaignFrame];
          if (frameData.startingEquipment && frameData.startingEquipment.armor) {
            return frameData.startingEquipment.armor.find(armor => armor.id === armorId);
          }
        }

        return null;
      };

      const armorData = getArmorData(newCharacter.equipment.armor);

      // Extract Foundation features from subclass
      const subclassData = allData.subclasses?.[newCharacter.subclass];
      const foundationFeatures = subclassData?.features
        ?.filter(feat => feat.type === 'Foundation')
        .map(feat => feat.name) || [];

      // Build starting inventory items
      const startingInventoryItems = [
        { name: "Torch", description: "Useful for illuminating a dark room", type: "utility" },
        { name: "50 feet of rope", description: "Useful for climbing a wall or rappelling down a cliff", type: "utility" },
        { name: "Basic supplies", description: "Tent, bedroll, tinderbox, rations, etc.", type: "utility" }
      ];

      // Add selected potion
      if (newCharacter.startingItems?.potion) {
        if (newCharacter.startingItems.potion === 'minor-health-potion') {
          startingInventoryItems.push({
            name: "Minor Health Potion",
            description: "Clear 1d4 Hit Points when consumed",
            type: "consumable"
          });
        } else if (newCharacter.startingItems.potion === 'minor-stamina-potion') {
          startingInventoryItems.push({
            name: "Minor Stamina Potion",
            description: "Clear 1d4 Stress when consumed",
            type: "consumable"
          });
        }
      }

      // Add selected class item
      if (newCharacter.startingItems?.classItem) {
        startingInventoryItems.push({
          name: newCharacter.startingItems.classItem,
          description: `Special item for ${classData.name}`,
          type: "class-specific"
        });
      }

      const finalCharacter = {
        ...newCharacter,
        maxHp: classData.startingHP,
        hp: classData.startingHP,
        maxStress: 6,
        stress: 0,
        hope: 2,
        maxHope: 6,
        proficiency: 1,
        evasion: classData.startingEvasion || 10,
        armorMarks: [],
        conditions: [],
        traitMarks: { Agility: 0, Strength: 0, Finesse: 0, Instinct: 0, Presence: 0, Knowledge: 0 },
        gold: { handfuls: 1, bags: 0, chests: 0 },
        inventory: { equipment: [], items: startingInventoryItems, ingredients: [] },
        damageThresholds: armorData
          ? {
              major: armorData.baseThresholds.minor + 1,
              severe: armorData.baseThresholds.major + 1,
            }
          : { major: 9, severe: 19 },
        loadout: newCharacter.loadout || newCharacter.domainCards || [],
        vault: [],
        subclassFeatures: {
          foundation: foundationFeatures,
          specializations: [],
          masteries: []
        },
        slayerDice: [] // Warrior "Slayer" Foundation feature
      };

      // Check if we're editing an existing character or creating a new one
      if (characterId && characterId !== 'new') {
        // EDITING: Update existing character
        const characterRef = doc(db, 'characters', characterId);
        const { id, ...dataToSave } = finalCharacter;

        await updateDoc(characterRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        });

        console.log('Character updated with ID:', characterId);
        toast.success(`${newCharacter.name} has been updated!`);

        // Navigate back to the character sheet
        navigate(`/character/${characterId}`);
      } else {
        // CREATING: Save new character to Firestore
        const charactersRef = collection(db, 'characters');
        const { id, ...dataToSave } = finalCharacter;

        const docRef = await addDoc(charactersRef, {
          ...dataToSave,
          userId: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        console.log('Character created with ID:', docRef.id);
        toast.success(`${newCharacter.name} has been forged! Welcome to Level 1.`);
        setTimeout(() => {
          toast('Add your character to a campaign from the Campaigns tab!', { duration: 8000, icon: '⚔️' });
        }, 1500);

        // Track character creation in analytics
        trackCharacterCreated(newCharacter);

        // Navigate to the new character sheet
        navigate(`/character/${docRef.id}`);
      }
    } catch (error) {
      console.error(`Error ${characterId ? 'updating' : 'creating'} character:`, error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      let errorMessage = `Failed to ${characterId ? 'update' : 'save'} character. `;
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied - please check Firestore security rules.';
      } else if (error.code === 'unavailable') {
        errorMessage += 'Database unavailable - please try again.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }

      toast.error(errorMessage);
    }
  };

  return <CharacterBuilder onCharacterForge={handleCharacterForge} />;
}


// Component for Campaign Creator route
function CampaignCreatorRoute() {
  return <CampaignCreator />;
}

// Component for Campaign View route
function CampaignViewRoute() {
  const { campaignId } = useParams();
  return (
    <DataProvider>
      <CampaignView campaignId={campaignId} />
    </DataProvider>
  );
}

// Component for Join Campaign route
function JoinCampaignRoute() {
  const { campaignId } = useParams();
  // This would redirect to CampaignView with join mode
  // For now, just navigate to the campaign view
  return (
    <DataProvider>
      <CampaignView campaignId={campaignId} isJoining={true} />
    </DataProvider>
  );
}

// Component for Character Sheet route
function CharacterSheetRoute() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [characterData, setCharacterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowedToView, setAllowedToView] = useState(false);

  // Auto-detect which campaign this character belongs to (same as TabbedCharacterSheet)
  const { characterCampaignId } = useCharacterCampaign(characterId, user);

  useEffect(() => {
    const loadCharacter = async () => {
      if (!characterId || !user) return;
      
      try {
        const characterRef = doc(db, 'characters', characterId);
        const characterDoc = await getDoc(characterRef);
        
        if (characterDoc.exists()) {
          const data = { id: characterDoc.id, ...characterDoc.data() };
          
          // Check if user owns this character
          const isOwner = data.userId === user.uid;
          
          // Check if user is GM of any campaign containing this character
          let isGM = false;
          if (!isOwner) {
            const campaignsQuery = query(
              collection(db, 'campaigns'),
              where('gmId', '==', user.uid)
            );
            const campaignsSnapshot = await getDocs(campaignsQuery);
            
            for (const campaignDoc of campaignsSnapshot.docs) {
              const campaignData = campaignDoc.data();
              if (campaignData.players?.some(player => player.characterId === characterId)) {
                isGM = true;
                break;
              }
            }
          }
          
          // Allow access if user owns the character OR is GM of a campaign containing it
          if (isOwner || isGM) {
            setCharacterData(data);
            setAllowedToView(true);
          } else {
            // Unauthorized access - redirect to character list
            console.log('Unauthorized access to character:', characterId);
            navigate('/');
          }
        } else {
          // Character not found - redirect to character list
          navigate('/');
        }
      } catch (error) {
        console.error('Error loading character:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadCharacter();
  }, [characterId, user, navigate]);

  // Least-invasive live sync: attach an onSnapshot listener once access is granted
  useEffect(() => {
    if (!allowedToView || !characterId) return;
    const characterRef = doc(db, 'characters', characterId);
    const unsubscribe = onSnapshot(characterRef, (snap) => {
      if (snap.exists()) {
        setCharacterData({ id: snap.id, ...snap.data() });
      }
    }, (err) => {
      console.error('onSnapshot error for character:', characterId, err);
    });
    return () => unsubscribe();
  }, [allowedToView, characterId]);

  // Helper to recursively remove undefined values (Firebase doesn't accept undefined)
  const sanitizeForFirestore = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForFirestore(item));
    }
    if (typeof obj === 'object') {
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          result[key] = sanitizeForFirestore(value);
        }
      }
      return result;
    }
    return obj;
  };

  const handleCharacterUpdate = async (updatedCharacter) => {
    try {
      if (updatedCharacter.id) {
        const characterRef = doc(db, 'characters', updatedCharacter.id);
        // Remove id field before updating (Firestore doesn't allow id in document data)
        const { id, ...dataToSave } = updatedCharacter;
        // Sanitize to remove undefined values (Firestore doesn't accept them)
        const sanitizedData = sanitizeForFirestore(dataToSave);
        await updateDoc(characterRef, {
          ...sanitizedData,
          updatedAt: serverTimestamp()
        });
        setCharacterData(updatedCharacter);
      }
    } catch (error) {
      console.error('Error updating character:', error);
      toast.error('Failed to save character changes. Please try again.');
    }
  };

  const handleExitToMenu = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading character...</p>
        </div>
      </div>
    );
  }

  if (!characterData) {
    return <Navigate to="/" replace />;
  }

  return (
    <DataProvider>
      {characterCampaignId ? (
        <CampaignProvider campaignId={characterCampaignId}>
          {/* New tabbed interface with built-in navigation, wrapped in Campaign Context */}
          <TabbedCharacterSheet
            characterData={characterData}
            onCharacterUpdate={handleCharacterUpdate}
            onExit={handleExitToMenu}
          />
        </CampaignProvider>
      ) : (
        /* New tabbed interface without Campaign Context (solo play) */
        <TabbedCharacterSheet
          characterData={characterData}
          onCharacterUpdate={handleCharacterUpdate}
          onExit={handleExitToMenu}
        />
      )}
    </DataProvider>
  );
}

function AppContent() {
  const { user, loading, betaStatus, betaLoading } = useAuth();

  // Show loading spinner while checking authentication or beta status
  if (loading || (user && betaLoading)) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {loading ? 'Loading...' : 'Checking access...'}
          </p>
        </div>
      </div>
    );
  }

  // Show landing page and public routes for unauthenticated users
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/about" element={<About />} />
        <Route path="/login" element={<Login />} />
        {/* Show login for unknown routes - preserves URL so post-auth lands on intended page */}
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Beta gate: DISABLED for open beta - all authenticated users get access
  // Keep for reference: can re-enable to block specific users (rejected/revoked)
  // if (betaStatus === 'rejected' || betaStatus === 'revoked') {
  //   return <BetaAccessPending status={betaStatus} />;
  // }

  // Detect dev environment by hostname
  const isDev = window.location.hostname.includes('chaggerheartdev') ||
                window.location.hostname === 'localhost';

  return (
    <>
      {/* DEV Environment Banner */}
      {isDev && (
        <div className="fixed bottom-4 right-4 z-50 bg-yellow-500 text-black text-xs py-2 px-3 font-bold shadow-lg rounded-lg">
          DEV
        </div>
      )}
      <Routes>
        <Route path="/" element={<CharacterListRoute />} />
        <Route path="/characters/:characterId/build" element={
        <DataProvider>
          <CharacterBuilderRoute />
        </DataProvider>
      } />
      <Route path="/character/:characterId" element={<CharacterSheetRoute />} />
      <Route path="/character/:characterId/companion/create" element={
        <SimplePageLayout>
          <CompanionCreator />
        </SimplePageLayout>
      } />
      <Route path="/character/:characterId/companion" element={
        <SimplePageLayout>
          <CompanionSheet />
        </SimplePageLayout>
      } />
      <Route path="/create-campaign" element={
        <SimplePageLayout>
          <DataProvider>
            <CampaignCreatorRoute />
          </DataProvider>
        </SimplePageLayout>
      } />
      <Route path="/campaign/:campaignId" element={<CampaignViewRoute />} />
      <Route path="/join-campaign/:campaignId" element={<JoinCampaignRoute />} />
      <Route path="/animation-demo" element={
        <DataProvider>
          <BeastFeastAnimationDemo />
        </DataProvider>
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/about" element={<About />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppContent />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
